import { useEffect, useRef, useState } from "react";
import { BiteData, BiteAttempt } from "../../types/data";
import { BiteStatus } from "../../types/enums";
import { BITE_TYPE } from "@/features/notepad/constants/teaching";
import BiteCard from "./BiteCard";
import PracticeHostPreview from "./PracticeHostPreview";
import { useBiteAttemptsStore } from "../../stores/biteAttemptsStore";
import { TutorSupportLevel } from "../../types/enums";
import { getScreenshotUrl } from "../../utils/screenshotCache";
import { buildAttemptId } from "../../utils/attemptId";
import { useScrollHint } from "../../../../hooks/useScrollHint";

const SUPPORT_LEVEL_RANK: Record<string, number> = {
  [TutorSupportLevel.FLAWLESS]: 0,
  [TutorSupportLevel.LOW]: 1,
  [TutorSupportLevel.MEDIUM]: 2,
  [TutorSupportLevel.HIGH]: 3,
};

/** Return the index (in sortedAttempts) of the "best" completed attempt. */
function bestAttemptIndex(attempts: BiteAttempt[]): number | null {
  let bestIdx: number | null = null;
  let bestRank = Infinity;
  attempts.forEach((a, i) => {
    if (a.biteStatus !== BiteStatus.COMPLETED) return;
    const rank = a.tutorSupportLevel
      ? (SUPPORT_LEVEL_RANK[a.tutorSupportLevel] ?? 99)
      : 99;
    if (rank < bestRank) {
      bestRank = rank;
      bestIdx = i;
    }
  });
  return bestIdx;
}

const BiteOverview = ({
  bite,
  bites,
  name,
  lessonName = "Lesson",
}: {
  bite: BiteData;
  bites: BiteData[];
  name: string;
  lessonName?: string;
}) => {
  // Parse session_id and slide_idx from biteId (format: "session_id:slideIdx")
  const [sessionId, slideIdxStr] = bite.biteId.split(":");
  const slideIdx = parseInt(slideIdxStr, 10);

  const { fetchAttempts } = useBiteAttemptsStore();
  const [loadedAttempts, setLoadedAttempts] = useState<BiteAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const isPractice = bite.biteType === BITE_TYPE.PRACTICE;
  const [isHostDiagramMissing, setIsHostDiagramMissing] = useState(false);
  const {
    ref: scrollRef,
    hasMore: hasMoreBelow,
    onScroll: checkScroll,
    checkScroll: checkScrollImmediate,
  } = useScrollHint<HTMLDivElement>({ axis: "vertical" });

  // Fetch attempts when the drawer opens, and again whenever this bite's
  // isProcessing flips true -> false while it stays open — that's the only
  // moment the store's cache (invalidated by the poll on that same flip) has
  // something new to return. Re-running while isProcessing is still true, or
  // when it goes false -> true, would just re-request the same answer.
  const prevIsProcessingRef = useRef<boolean | undefined>(undefined);
  // Bumped per loadAttempts() invocation so a call that settles after a newer
  // one has started (e.g. the mount-time fetch for a still-processing bite,
  // outlived by the fetch its own resolution triggers) can tell it's stale
  // and skip clearing isLoading — otherwise it would flash the old/incomplete
  // attempts as final before the newer call's answer lands.
  const requestIdRef = useRef(0);
  useEffect(() => {
    const shouldSkip =
      prevIsProcessingRef.current === false && bite.isProcessing === true;
    prevIsProcessingRef.current = bite.isProcessing;
    if (shouldSkip) return;

    const loadAttempts = async () => {
      const myRequestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const apiAttempts = await fetchAttempts(sessionId, slideIdx);
        // null means this call lost the race to a newer request for the
        // same bite and the cache doesn't have that newer answer yet —
        // leave whatever's currently shown alone; the run that owns the
        // newer request will update it once it lands.
        if (apiAttempts === null) return;
        // Map API attempts to BiteAttempt format
        const mapped: BiteAttempt[] = apiAttempts.map((a) => ({
          id: buildAttemptId(sessionId, slideIdx, a.slide_end_ts),
          startTime: a.slide_start_ts ?? a.slide_end_ts ?? 0,
          biteStatus:
            a.bite_status === "completed"
              ? BiteStatus.COMPLETED
              : BiteStatus.INCOMPLETE,
          // null (not measured) must stay distinguishable from false, so it
          // becomes undefined rather than being coerced.
          hasInstructionalHelp:
            typeof a.instructional_help === "boolean"
              ? a.instructional_help
              : undefined,
          tutorSupportLevel:
            a.tutor_support_level?.toLowerCase() as BiteAttempt["tutorSupportLevel"],
          studentPerformance:
            a.attempts_total > 0 || a.time_spent_ms > 0
              ? {
                  studentAsked: a.student_asked_count,
                  AIAsked: a.ai_asked_count,
                  timeSpent: a.time_spent_ms,
                  incorrectAttempts: a.attempts_incorrect,
                  totalAttempts: a.attempts_total,
                }
              : undefined,
        }));
        setLoadedAttempts(mapped);
      } catch (e) {
        console.error("Failed to load attempts", e);
      } finally {
        if (myRequestId === requestIdRef.current) {
          setIsLoading(false);
          requestAnimationFrame(checkScrollImmediate);
        }
      }
    };

    loadAttempts();
  }, [sessionId, slideIdx, fetchAttempts, bite.isProcessing]);

  // Screenshot is independent of processing state — fetch once per bite, not
  // on every isProcessing flip.
  useEffect(() => {
    // Not fetched for practice: the capture targets `.reveal`, and the notepad
    // canvas mounts beside it, so the shot never contains the student's work.
    if (!isPractice) {
      getScreenshotUrl(sessionId, slideIdx).then((url) =>
        setScreenshotUrl(url),
      );
    }
  }, [sessionId, slideIdx, isPractice]);

  // Use loaded attempts if available, otherwise fall back to bite.attempts
  const attemptsToShow =
    loadedAttempts.length > 0 ? loadedAttempts : bite.attempts;
  const sortedAttempts = [...attemptsToShow].sort((a, b) => {
    const rankA = a.tutorSupportLevel
      ? (SUPPORT_LEVEL_RANK[a.tutorSupportLevel] ?? 99)
      : 99;
    const rankB = b.tutorSupportLevel
      ? (SUPPORT_LEVEL_RANK[b.tutorSupportLevel] ?? 99)
      : 99;
    return rankA - rankB;
  });

  // Map each attempt to its chronological order (1-based)
  const chronologicalOrder = new Map(
    [...attemptsToShow]
      .sort((a, b) => a.startTime - b.startTime)
      .map((a, i) => [a.id, i + 1]),
  );

  const bestIdx =
    sortedAttempts.length > 1 ? bestAttemptIndex(sortedAttempts) : null;

  const getStatusLabel = () => {
    if (bite.biteStatus === BiteStatus.COMPLETED) return "Completed";
    if (bite.biteStatus === BiteStatus.INCOMPLETE) return "Incomplete";
    return "Not started";
  };

  // ---- Right panel: attempts list ----
  const attemptsPanel = (
    <div className="flex flex-col gap-3 w-full md:w-[280px]">
      <div className="flex flex-col gap-1">
        <span className="font-mulish font-bold text-[15px] leading-none text-[#02734A]">
          {bite.biteIndex} of {bites.length} Bites
        </span>
        {sortedAttempts.length > 1 && (
          <span className="font-mulish font-semibold et-analytics-caption text-[10px] leading-none text-[#252525]">
            {name} attempted the bite {sortedAttempts.length} times
          </span>
        )}
        {hasMoreBelow && (
          <span className="font-mulish et-analytics-caption text-[10px] leading-none et-analytics-muted text-[#25252566]">
            Scroll the list to see all {sortedAttempts.length} attempts ↓
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-[#25252599] text-sm py-2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-sagegreen rounded-full animate-spin" />
          Loading attempts...
        </div>
      ) : sortedAttempts.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex flex-col gap-[10px] overflow-y-auto max-h-[340px] rounded-[10px] border border-gray-200 p-[10px]"
          >
            {sortedAttempts.map((attempt, index) => {
              const displayIndex =
                chronologicalOrder.get(attempt.id) ?? index + 1;
              return (
                <BiteCard
                  key={attempt.id}
                  bite={bite}
                  attempt={attempt}
                  index={displayIndex}
                  studentName={name}
                  lessonName={lessonName}
                  biteIndex={bite.biteIndex}
                  isBest={bestIdx === index}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-[#25252599] text-sm py-2">
          Status: {getStatusLabel()}
        </div>
      )}
      {bite.isLastLearned && (
        <div className="text-sagegreen text-sm italic mt-2">Last viewed</div>
      )}
    </div>
  );

  // A practice bite is identified by its diagram or not at all: with no host
  // diagram PracticeHostPreview renders nothing and the panel collapses.
  const previewBox = isPractice ? (
    isHostDiagramMissing ? null : (
      <PracticeHostPreview
        sessionId={sessionId}
        slideIdx={slideIdx}
        onUnavailable={() => setIsHostDiagramMissing(true)}
      />
    )
  ) : screenshotUrl ? (
    <img
      src={screenshotUrl}
      alt="Slide preview"
      className="w-full h-full object-contain"
    />
  ) : null;

  return (
    <div className="flex flex-col md:flex-row gap-[10px] justify-center">
      {previewBox && (
        <div className="flex-shrink-0 w-full md:w-[460px] h-[200px] md:h-[340px] rounded-[10px] border-2 border-[#ECECEC] overflow-hidden bg-gray-50 flex items-center justify-center">
          {previewBox}
        </div>
      )}
      {attemptsPanel}
    </div>
  );
};

export default BiteOverview;
